import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
// v2.4.0 — compose: html upload, tags, send-to-list, campaign runner
import useStore, { api } from './store/useStore';

// ── Tag substitution (shared with CampaignRunner) ─────────────────────────────
export function applyTags(template, row, senderName, senderEmail) {
  let r = template || '';
  const now = new Date();
  const entries = {
    ...row,
    date: now.toLocaleDateString(),
    time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    sender_name: senderName || '',
    sender_email: senderEmail || '',
  };
  for (const [k, v] of Object.entries(entries)) {
    const val = String(v || '');
    r = r.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'gi'), val)
         .replace(new RegExp(`\\[${k}\\]`, 'gi'), val)
         .replace(new RegExp(`%${k}%`, 'gi'), val);
  }
  return r;
}

// ── Campaign runner — invisible component, lives at App level ─────────────────
function CampaignRunner() {
  const { campaign, advanceCampaign } = useStore();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!campaign || !campaign.running || campaign.index >= campaign.total) return;
    const delay = campaign.index === 0 ? 500 : (campaign.interval || 60) * 1000;

    timerRef.current = setTimeout(async () => {
      const item = campaign.items[campaign.index];
      if (!item) { advanceCampaign(false, '', 'Missing item'); return; }
      const body = applyTags(campaign.bodyTemplate, item, campaign.sendAsName, campaign.senderEmail);
      const subject = applyTags(campaign.subject, item, campaign.sendAsName, campaign.senderEmail);
      try {
        await api.post(`/api/emails/${campaign.accountId}/send`, {
          to: item.email, subject, body,
          send_as_name: campaign.sendAsName || '',
          cc: '', bcc: '', reply_to: '',
        });
        advanceCampaign(true, item.email, null);
      } catch (e) {
        advanceCampaign(false, item.email, e?.response?.data?.detail || 'Send failed');
      }
    }, delay);

    return () => clearTimeout(timerRef.current);
  }, [campaign?.index, campaign?.running]);

  return null;
}

// ── Floating campaign progress widget ─────────────────────────────────────────
function FloatingProgress() {
  const { campaign, pauseCampaign, resumeCampaign, stopCampaign } = useStore();
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('campaign-pill-pos') || 'null');
      if (saved) return saved;
    } catch {}
    return { top: 16, left: Math.max(0, window.innerWidth - 280) };
  });
  const dragging = useRef(false);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX - pos.left;
    const startY = e.clientY - pos.top;
    const onMove = (e) => {
      if (!dragging.current) return;
      setPos({
        top: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - startY)),
        left: Math.max(0, Math.min(window.innerWidth - 264, e.clientX - startX)),
      });
    };
    const onUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setPos(p => { localStorage.setItem('campaign-pill-pos', JSON.stringify(p)); return p; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!campaign) return null;

  const { total, sent, failed, running, index, subject, accountEmail, failedItems } = campaign;
  const done = index >= total;
  const pct = total > 0 ? Math.round((index / total) * 100) : 0;
  const borderColor = done ? '#2ea043' : running ? '#1f6feb' : '#d29922';

  return (
    <div
      onMouseDown={onMouseDown}
      style={{
        position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
        background: '#161b22', border: `1px solid ${borderColor}`,
        borderRadius: 10, width: expanded ? 300 : 264,
        boxShadow: '0 4px 24px rgba(0,0,0,.7)',
        fontFamily: "'Segoe UI', -apple-system, sans-serif",
        transition: 'width .2s',
        cursor: 'grab',
        userSelect: 'none',
      }}>
      {/* Header row */}
      <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(v => !v)}>
        <span style={{ fontSize: 14 }}>{done ? '✅' : running ? '📤' : '⏸'}</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {done ? 'Campaign complete' : running ? 'Sending…' : 'Paused'}
        </span>
        <span style={{ fontSize: 10, color: '#6e7681' }}>{expanded ? '▲' : '▼'}</span>
        <button onClick={e => { e.stopPropagation(); stopCampaign(); }}
          style={{ background: 'none', border: 'none', color: '#6e7681', cursor: 'pointer', fontSize: 13, padding: '2px 4px', borderRadius: 3, lineHeight: 1 }}
          onMouseEnter={e => e.currentTarget.style.color = '#f85149'}
          onMouseLeave={e => e.currentTarget.style.color = '#6e7681'}>✕</button>
      </div>

      {/* Progress bar */}
      <div style={{ margin: '0 12px 6px', background: '#21262d', borderRadius: 3, height: 4 }}>
        <div style={{ background: done ? '#2ea043' : '#1f6feb', height: 4, borderRadius: 3, width: `${pct}%`, transition: 'width .4s' }} />
      </div>

      {/* Stats row */}
      <div style={{ padding: '0 12px 8px', display: 'flex', gap: 12, fontSize: 11 }}>
        <span style={{ color: '#8b949e' }}>Total <b style={{ color: '#e6edf3' }}>{total}</b></span>
        <span style={{ color: '#3fb950' }}>Sent <b>{sent}</b></span>
        <span style={{ color: '#f85149' }}>Failed <b>{failed}</b></span>
        <span style={{ color: '#6e7681', marginLeft: 'auto' }}>{pct}%</span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: '1px solid #21262d', padding: '8px 12px' }}>
          {subject && <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Subject: {subject}</div>}
          {accountEmail && <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>From: {accountEmail}</div>}
          {!done && (
            <div style={{ display: 'flex', gap: 6, marginBottom: failedItems?.length ? 8 : 0 }}>
              {running ? (
                <button onClick={pauseCampaign} style={{ flex: 1, background: '#21262d', border: '1px solid #30363d', borderRadius: 6, color: '#e6edf3', fontSize: 11, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>⏸ Pause</button>
              ) : (
                <button onClick={resumeCampaign} style={{ flex: 1, background: '#238636', border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>▶ Resume</button>
              )}
              <button onClick={stopCampaign} style={{ flex: 1, background: 'transparent', border: '1px solid #f85149', borderRadius: 6, color: '#f85149', fontSize: 11, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>✕ Stop</button>
            </div>
          )}
          {failedItems?.length > 0 && (
            <div style={{ maxHeight: 90, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#8b949e', marginBottom: 4 }}>Failed sends:</div>
              {failedItems.map((fi, i) => (
                <div key={i} style={{ fontSize: 10, color: '#f85149', padding: '2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fi.email} — {fi.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
import Login from './pages/Login';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminDashboard from './pages/AdminDashboard';
import EmailClient from './pages/EmailClient';
import EmailExtractor from './pages/EmailExtractor';
import Connect from './pages/Connect';

function RequireAuth({ children, role }) {
  const user = useStore(s => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const user = useStore(s => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'superadmin') return <Navigate to="/superadmin" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { fetchMe, fetchSettings } = useStore();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('mv_token');
    if (token) {
      // Snapshot campaign before auth API calls — fetchMe/fetchSettings both
      // call set() which triggers a persist save; if rehydration hasn't merged
      // yet those saves would overwrite a valid campaign with null.
      const preCampaign = useStore.getState().campaign;
      fetchMe().then(user => {
        if (user) {
          // Silently renew the JWT so the session rolls forward on every page load
          api.post('/auth/refresh').then(r => {
            if (r?.data?.access_token) localStorage.setItem('mv_token', r.data.access_token);
          }).catch(() => {});
          return fetchSettings().catch(() => {});
        }
        // Token expired or invalid — clear it; RequireAuth will redirect to /login via React Router
        localStorage.removeItem('mv_token');
      }).finally(() => {
        if (preCampaign && !useStore.getState().campaign) {
          useStore.setState({ campaign: preCampaign });
        }
        setReady(true);
      });
    } else {
      setReady(true);
    }
  }, []);

  if (!ready) return null;

  return (
    <BrowserRouter>
      <CampaignRunner />
      <FloatingProgress />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/connect/:linkToken" element={<Connect />} />
        <Route path="/" element={<RoleRedirect />} />
        <Route path="/superadmin/*" element={
          <RequireAuth role="superadmin"><SuperAdminDashboard /></RequireAuth>
        } />
        <Route path="/dashboard/*" element={
          <RequireAuth><AdminDashboard /></RequireAuth>
        } />
        <Route path="/email/extractor/results" element={
          <RequireAuth><EmailExtractor /></RequireAuth>
        } />
        <Route path="/email/extractor" element={
          <RequireAuth><EmailExtractor /></RequireAuth>
        } />
        <Route path="/email" element={
          <RequireAuth><EmailClient /></RequireAuth>
        } />
        <Route path="/email/:accountId" element={
          <RequireAuth><EmailClient /></RequireAuth>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
